/**
 * The list of globals the Worker deletes, and a lint that is NOT the boundary.
 *
 * ## Read this before changing anything in this file
 *
 * Scripts are shared between traders. A trader installs code a stranger wrote,
 * from a share code they were given in a chat room, and runs it inside an
 * application that is simultaneously holding their broker session, their
 * journal, and their subscription. The only thing standing between that
 * stranger and all of it is the sandbox.
 *
 * The boundary is the WORKER — a thread with no DOM, no window, no cookies, no
 * access to the application's memory, from which the globals below have been
 * deleted before a single character of user code is evaluated, and which is
 * handed nothing but numbers.
 *
 * `lintScriptSource` below is NOT part of that boundary. It reads the source
 * as text and points at things that will not work, so an author gets "fetch is
 * not available in a script" while they are writing instead of a ReferenceError
 * at run time. Any check performed on source text can be defeated by
 * `globalThis['fet' + 'ch']`, and a lint that is treated as a defence is worse
 * than no lint, because it invites someone to skip the deletions. If you find
 * yourself adding a pattern here to BLOCK something rather than to EXPLAIN it,
 * the change belongs in the Worker bootstrap instead.
 */

/**
 * Globals removed from the Worker's scope before user code is evaluated.
 *
 * Grouped by what each one would give away, because a list of names with no
 * reasons is a list nobody can safely edit later.
 */
export const DENIED_GLOBALS: readonly string[] = [
  /* Network. A script that can reach the network can send the trader's
     positions, or the contents of their chart, anywhere it likes. */
  'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource', 'Request', 'Response',
  'navigator', 'sendBeacon',

  /* Loading more code. `importScripts` fetches and executes a URL
     synchronously, which is both network access and an escape from every
     review the source went through. */
  'importScripts', 'WebAssembly', 'Worker', 'SharedWorker',

  /* Storage. Nothing here is the script's to read or write, and IndexedDB in
     particular is shared with the application on the same origin. */
  'indexedDB', 'caches', 'localStorage', 'sessionStorage', 'cookieStore',

  /* Identity and randomness that is also a fingerprint. `crypto` is removed
     rather than trimmed: `subtle` is a capability, and `getRandomValues` is
     not worth the surface. A script that wants noise can write its own PRNG. */
  'crypto', 'Notification', 'clients', 'registration', 'PushManager',

  /* Talking to the host. The bootstrap keeps its own reference to the real
     `postMessage` in a closure and then removes this one, so a script cannot
     forge a result message, cannot answer for a run it was not asked to do,
     and cannot kill its own thread to escape the timeout. */
  'postMessage', 'close', 'onmessage', 'addEventListener', 'removeEventListener',
];

/**
 * Things that are NOT removed, and why — so nobody "hardens" them later.
 *
 * - `Math`, `Number`, `String`, `Array`, `Object`, `JSON`, `Map`, `Set`, `Date`
 *   are what an indicator is written in.
 * - `console` stays: it is how an author debugs, the Worker's console goes to
 *   the developer tools of the person running it, and it carries no capability.
 * - `setTimeout` stays available but is pointless — a run is one synchronous
 *   call and its result is read when the function returns, so a script that
 *   defers work simply produces nothing.
 */
export const KEPT_GLOBALS: readonly string[] = [
  'Math', 'Number', 'String', 'Array', 'Object', 'JSON', 'Map', 'Set', 'Date', 'console',
];

/**
 * The named parameters of a script's function body.
 *
 * A script is compiled as a function taking these, so inside the body they are
 * ORDINARY LOCAL VARIABLES that shadow anything of the same name in the global
 * scope. That matters for two of them specifically: `close` and `open` are also
 * globals — `self.close()` terminates a Worker — and both appear in the
 * denied list. The shadowing is what makes it safe to give a script the obvious
 * names rather than making every author write `bars.close`.
 *
 * The lint skips these for the same reason. A script that says `close[i]` is
 * reading its own parameter, and flagging it would fire on essentially every
 * correct indicator ever written.
 *
 * The ORDER is the calling convention. The Worker bootstrap builds its argument
 * list from this array, so a reordering here silently renames every series in
 * every installed script — `script.test.ts` pins it for that reason.
 */
export const SCRIPT_ARGS: readonly string[] = [
  'time', 'open', 'high', 'low', 'close', 'volume', 'inputs', 'instrument',
];

export interface ScriptLintNote {
  /** The identifier that will not resolve, or the construct that will fail. */
  readonly name: string;
  /** One line, addressed to the person writing the script. */
  readonly message: string;
}

/* Word-boundary matches only. `refetch()` is somebody's own helper, and
   flagging it would train authors to ignore the notes. */
function mentions(source: string, name: string): boolean {
  return new RegExp(`\\b${name}\\b`).test(source);
}

/**
 * Notes for the author about code that will not run.
 *
 * Advisory. See the file header: this cannot and does not stop anything.
 */
export function lintScriptSource(source: string): readonly ScriptLintNote[] {
  const notes: ScriptLintNote[] = [];

  for (const name of DENIED_GLOBALS) {
    /* Shadowed by a parameter of the same name — see `SCRIPT_ARGS`. `close`
       is the clearest case: it is both a Worker global and the series every
       indicator reads. */
    if (SCRIPT_ARGS.includes(name)) continue;
    if (mentions(source, name)) {
      notes.push({
        name,
        message: `${name} is not available to a script. Scripts get bars and their own inputs, nothing else.`,
      });
    }
  }

  /* Module syntax. `new Function` compiles the body as a plain script, so an
     `import` or `export` at the top level is a SyntaxError rather than a
     missing module — an error message that sends the author looking in
     entirely the wrong place. */
  if (/^\s*(import|export)\b/m.test(source)) {
    notes.push({
      name: 'import',
      message: 'A script is one self-contained function body. import and export are not available.',
    });
  }
  if (mentions(source, 'require')) {
    notes.push({
      name: 'require',
      message: 'There are no modules to require. Write the helper you need inside the script.',
    });
  }

  return notes;
}
